/**
 * View component for /content-modules/:contentModuleId
 *
 * Displays a single contentModule from the 'byId' map in the contentModule reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, history, withRouter } from 'react-router-dom';

// import third-party libraries
import classNames from 'classnames';
import renderHTML from 'react-render-html';
import { DateTime } from 'luxon';

// import actions
import * as certificateTemplateActions from '../../certificateTemplate/certificateTemplateActions';
import * as evaluationActions from '../../evaluation/evaluationActions';
import * as metricActions from '../../metric/metricActions';
import * as nodeActions from '../../node/nodeActions';
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as studyActions from '../../study/studyActions';
import * as resourceLinkActions from '../../resourceLink/resourceLinkActions';
import * as contentModuleActions from '../contentModuleActions';

// import global components
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from "../../../global/components/routing/Breadcrumbs.js.jsx";
import VideoPlayer from "../../../global/components/helpers/VideoPlayer.js.jsx";

// import utilities
import richContentUtils from '../../../global/utils/richContentUtils';
import videoUtils from "../../../global/utils/videoUtils.js";

// import module components
import AdminResourceLinkListItem from '../../resourceLink/components/AdminResourceLinkListItem.js.jsx';
import AdminEvaluationQuickListItem from '../../evaluation/components/AdminEvaluationQuickListItem.js.jsx';
import MetricListItem from '../../metric/components/MetricListItem.js.jsx';
import ResourceLinkModal from '../../resourceLink/components/ResourceLinkModal.js.jsx';
import ContentModuleLayout from '../components/ContentModuleLayout.js.jsx';

// import contentModule css modules
import contentModuleStyles from '../contentModuleModuleStyles.css';

const simUrl = window.simUrl

class AdminSingleContentModule extends Base {
  constructor(props) {
    super(props);
    this.state = {
      createLinkModalOpen: false
    }
    this._bind(
      '_handleCreateEvaluation'
      , '_handleCopyEvaluation'
      , '_onVideoComplete'
    )
  }

  componentDidMount() {
    const { dispatch, history, match } = this.props;
    dispatch(contentModuleActions.fetchSingleIfNeeded(match.params.contentModuleId)).then((action) => {
      if(action.success) {
        dispatch(nodeActions.fetchSingleIfNeeded(action.item._startNode));
        if(action.item._organization) {
          dispatch(sponsorActions.fetchSingleIfNeeded(action.item._organization)); 
        }
      } else {
        alert(`ERROR: ${action.error}`)
        history.push('/admin/authoring/content-modules');
      }
    });
    dispatch(metricActions.fetchListIfNeeded('content-module', match.params.contentModuleId));
    dispatch(certificateTemplateActions.fetchListIfNeeded('all'));
    dispatch(evaluationActions.fetchListIfNeeded('_contentModule', match.params.contentModuleId ));
    dispatch(resourceLinkActions.fetchListIfNeeded('contentModule', match.params.contentModuleId));
    dispatch(studyActions.fetchListIfNeeded('content-module', match.params.contentModuleId));

  }

  componentWillReceiveProps(nextProps) {
    // console.log("compone")
    const { dispatch, match } = nextProps;
    dispatch(evaluationActions.fetchListIfNeeded('_contentModule', match.params.contentModuleId));
    dispatch(resourceLinkActions.fetchListIfNeeded('contentModule', match.params.contentModuleId));
  }

  _handleCreateEvaluation() {
    const { dispatch, evaluationStore, match, contentModuleStore } = this.props;
    const evaluations = evaluationStore.util.getList('_contentModule', match.params.contentModuleId);
    const selectedContentModule = contentModuleStore.selected.getItem();
    let newEvaluation = _.cloneDeep(evaluationStore.defaultItem);
    newEvaluation._contentModule = match.params.contentModuleId;
    newEvaluation.title = 'Exam #'+(evaluations.length + 1)+' for ' + selectedContentModule.title;
    dispatch(evaluationActions.sendCreateEvaluation(newEvaluation)).then(action => {
      dispatch(evaluationActions.invalidateList('_contentModule', match.params.contentModuleId));
    })
  }

  _handleCopyEvaluation(id) {
    let prompt = confirm("Are you sure you want to create a new copy of this Content Module and all associated objects?")
    if(prompt) {
      console.log("copying evaluation", id);
      // todo: modal or something with a spinner
      this.props.dispatch(contentModuleActions.sendCopyContentModule(id)).then(action => {
        if(action.success) {
          this.props.history.push(`/admin/authoring/content-modules/${action.item._id}`);
        } else {
          alert("Error: " + action.error)
        }
      })
      alert("Please give this function a minute or two to run. This page will redirect you when it is completed.")
    }
  }

  _onVideoComplete(e) {
    console.log("1 VIDEO COMPLETED");
    console.log(e);
  }

  render() {
    // console.log("RENDER SINGLE CONTENT MODULE");
    const {
      certificateTemplateMap
      , evaluationStore
      , location
      , loggedInUser
      , match
      , metricStore 
      , nodeStore
      , sponsorStore
      , studyStore
      , resourceLinkStore
      , contentModuleStore
    } = this.props;

    /**
     * use the selected.getItem() utility to pull the actual contentModule object from the map
     */
    const selectedContentModule = contentModuleStore.selected.getItem();
    const selectedNode = nodeStore.selected.getItem();
    const evaluations = evaluationStore.util.getList('_contentModule', match.params.contentModuleId);
    const resourceLinks = resourceLinkStore.util.getList('contentModule', match.params.contentModuleId) || [];
    const sponsor = sponsorStore.selected.getItem();
    const studies = studyStore.util.getList('content-module', match.params.contentModuleId)
    const metrics = metricStore.util.getList('content-module', match.params.contentModuleId)
    const orderedMetrics = metrics ? metrics.sort((a,b) => a.order - b.order) : []

    const isEmpty = (
      !selectedContentModule
      || !selectedContentModule._id
      || contentModuleStore.selected.didInvalidate
    );

    const noLinks = (
      !resourceLinks
      || resourceLinks.length < 1
    )

    let partitionedEvals = []
    if(evaluations) {
      /**
       * split evaluations into two groups, 'archived' and 'NOT archived'
       * if the 'NOT archived' list is > 0, then don't let the user create any
       * more evaluations.
       * partitionedEvals[0] === 'archived' i.e. {status: 'archived'} is truthy
       * partitionedEvals[1] === 'NOT archived'
       */
      partitionedEvals = _.partition(evaluations, {status: 'archived'})
    }

    return (
      <AuthoringLayout>
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              <Breadcrumbs links={location.state.breadcrumbs} />
              { isEmpty ?
                (
                  contentModuleStore.isFetching ?
                  <div className="u-centerText">
                    <div className="loading"></div>
                  </div>
                  :
                  <h2>Empty.</h2>
                )
                :
                <div style={{ opacity: contentModuleStore.isFetching ? 0.5 : 1, width: "100%" }}>
                  <div className="yt-row with-gutters">
                    <div className="yt-col full s_60 m_70">
                      <div className="content-container">
                        <div className="yt-row space-between">
                          <p style={{fontSize: '24px', fontFamily: 'TradeGothic LT Bold'}}>
                            { selectedContentModule.title }
                          </p>
                          <div>
                            <Link className="yt-btn x-small info" to={`${match.url}/update`}>Update content</Link>
                            <a href={`${simUrl}/preview/content-modules/${selectedContentModule._id}/nodes/${selectedContentModule._startNode}`} className="yt-btn x-small info link" target="_blank">
                              Preview <i className="fa fa-external-link"/>
                            </a>
                          </div>
                        </div>
                        <hr/>
                        <div>
                          <div style={{marginBottom: '10px'}}>
                            <Link
                              className="yt-btn x-small warning"
                              to={`/admin/audit-logs/content-module/${selectedContentModule._id}`}
                              target="_blank"
                            >
                              Object History &rarr;
                            </Link>     
                          </div>                  
                          <p>
                            Created on {DateTime.fromISO(selectedContentModule.created).toLocaleString(DateTime.DATETIME_FULL)}
                            { selectedContentModule._createdBy && userMap[selectedContentModule._createdBy] ?
                              <span> by
                                <Link to={`/admin/users/${selectedContentModule._createdBy}`} target="_blank"> {userMap[selectedContentModule._createdBy].username}</Link>
                              </span>
                              : null
                            }
                          </p>
                          <p><small><em>Last updated {DateTime.fromISO(selectedContentModule.updated).toLocaleString(DateTime.DATETIME_FULL)}</em></small></p>
                          <p></p>
                          {
                            selectedContentModule.allowAnonymous ? 
                              <p><b>**ALLOWS ANONYMOUS ACCESS**</b></p>
                            : null
                          }
                          
                        </div>
                        <div style={{marginTop: '20px'}} >
                          <div>
                            <a href={`${window.authoringUrl}/content-modules/${match.params.contentModuleId}`} target="_blank" className="yt-btn small block info ">View Overview<i className="far fa-external-link"/></a>
                          </div>
                          <div style={{marginTop: '10px', marginBottom: '10px'}}>
                            <a href={`${window.authoringUrl}/content-modules/${match.params.contentModuleId}/build`} target="_blank" className="yt-btn primary small block info ">Build module<i className="far fa-external-link"/></a>
                          </div>
                          <div>
                            <Link to={`/cm-builder/${match.params.contentModuleId}`} target="_blank" className="yt-btn link small block info ">Build module in Legacy <i className="far fa-external-link"/></Link>
                          </div>
                        </div>
                      </div>
                      <div className="content-container">
                      <p><strong>Other Module Info</strong></p>
                      <hr/>
                      <a href={`${window.authoringUrl}/content-modules/${match.params.contentModuleId}?settingsTab=usage`} className="yt-btn small block info ">Program Usage<i className="far"/></a>
                      <hr/>
                      <a href={`${window.authoringUrl}/content-modules/${match.params.contentModuleId}?settingsTab=metrics`} className="yt-btn small block info ">Tracking Metrics<i className="far"/></a>
                      <hr/>
                      <a href={`${window.authoringUrl}/content-modules/${match.params.contentModuleId}?settingsTab=certs`} className="yt-btn small block info ">Certificate Settings<i className="far"/></a>

                        {/* <p><strong>This Module is currently part of the following Programs:</strong></p>
                        <hr/>
                        <ul>
                          { studies ?
                            studies.map((study, i) =>
                              <li key={study._id + '_' + i}>
                                <Link to={`/admin/content-management/studies/${study._id}`}>{study.name}</Link>
                              </li>
                            ) 
                          : 
                            null
                          }
                        </ul> */}
                      </div>
                      <div className="content-container">
                        <p><strong>Active evaluation</strong></p>
                        <hr/>
                        { selectedContentModule.testable ?
                          <div>

                            <div className="-quick-list">
                              { partitionedEvals[1] && partitionedEvals[1].length > 0 ?
                                partitionedEvals[1].map((evaluation, i) =>
                                  <AdminEvaluationQuickListItem
                                    evaluation={evaluation}
                                    key={evaluation._id + i}
                                    contentModule={selectedContentModule}
                                  />
                                )
                                : selectedContentModule.allowAnonymous ? 
                                  <p><em>You cannot attach an User Evaluation to an Anonymous Content Module.</em></p>
                                :
                                  <div>
                                    <p><em>No active evaluation yet</em></p>
                                    <button className="yt-btn small info block " onClick={this._handleCreateEvaluation}>Create evaluation</button>
                                  </div>
                              }
                            </div>
                            <br/>
                            <small>
                              <em><strong>NOTE: </strong> A content module should only have one evaluation under active development. </em>
                            </small>
                          </div>
                          :
                          <div>This content module is not testable.</div>
                        }
                      </div>
                      {/* <div className="content-container">
                        <p><strong>Certificates of completion info</strong></p>
                        <hr/>
                        { selectedContentModule.certificateDuration ?
                          <p>All new certificates of completion will expire {selectedContentModule.certificateDuration} years after the user's completion date.</p>
                          :
                          <p>New certificates of completion will <strong><em> never </em></strong> expire for any user who completes this module. </p>
                        }
                        <div className={{marginBottom: '20px'}}>
                          <p><strong>Certificate type</strong></p>
                          { selectedContentModule.certificateType }
                        </div>
                        <p><strong>Version</strong></p>
                        <p>{selectedContentModule.version ? selectedContentModule.version : "None"}</p>
                        <p><strong>Number of credits earned for this module</strong></p>
                        { selectedContentModule.certificateUnits }
                        { selectedContentModule.acrp_ppr_number ? 
                          <div>
                            <p><strong>ACRP PPR Number</strong></p>
                            <p>{selectedContentModule.acrp_ppr_number}</p>
                          </div>
                          :
                          null 
                        }
                        <br/>
                        <p><strong>Certificate Template</strong></p>
                        { selectedContentModule._certificateTemplate && certificateTemplateMap[selectedContentModule._certificateTemplate] ? 
                          <p>{certificateTemplateMap[selectedContentModule._certificateTemplate].name}</p>
                          :
                          <p>Standard PF template</p>
                        }
                        <Link className="yt-btn x-small info" to={`${match.url}/update#certs`}>Update</Link>
                      </div> */}
                      {/* <div className="content-container">
                        <p><strong>Tracking metrics</strong></p>
                        <hr/>
                        <p>The following metrics are currently used in this module</p>
                            
                        <div className="table-wrapper">
                          <table className="yt-table striped">
                            <thead>
                              <tr>
                                <th className="_10">
                                  Order
                                </th>
                                <th className="_70" >
                                  Metric
                                </th>
                                <th className="_10" >
                                  Last Modified
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {orderedMetrics && orderedMetrics.map((metric, i) =>
                                <MetricListItem key={metric._id + '_' + i} metric={metric} />
                              )}
                            </tbody>
                          </table>
                        </div>              
                      </div> */}
                      <div className="content-container">
                        <p><strong>Resource Links</strong></p>
                        <hr/>
                        <div className="resource-links">
                          { noLinks && resourceLinks ?
                            <em> No resource links yet </em>
                            :
                            resourceLinks.map((rLink, i) =>
                              <AdminResourceLinkListItem
                                dispatch={this.props.dispatch}
                                key={i}
                                refKey="_contentModule"
                                refId={rLink ? rLink._contentModule : null}
                                resourceLink={rLink}
                              />
                            )
                          }
                        </div>
                        <button className="yt-btn x-small success link" onClick={() => this.setState({createLinkModalOpen: true })}><i className="fas fa-plus"/> Add Resource</button>
                      </div>
                    </div>
                    <div className="yt-col full s_40 m_30">
                      <div className="content-container">
                        <p><strong>Options</strong></p>
                        <hr/>
                        <div>
                          <p className="">Type: {selectedContentModule.type ? _.capitalize(selectedContentModule.type) : "N/A"}</p>                
                        </div>
                        <div>
                          <p>Show Sentient Modal: {selectedContentModule.showSentimentModal ? "Yes" : "No"}</p>
                        </div>
                      </div>
                      <div className="content-container">
                        <p><strong>Branding Logo</strong></p>
                        <hr/>
                        <div style={{maxWidth: '200px', marginTop: '32px'}}>
                          <img src={selectedContentModule.brandingLogoUrl ? selectedContentModule.brandingLogoUrl : (sponsor && sponsor.logo) ? sponsor.logo : "/img/cert-icons/cert-icon-empty@2x.png"} />
                        </div>
                      </div>
                      <div className="content-container">
                        <p><strong>Copy Content Module</strong></p>
                        <hr/>
                        <div className="resource-links">
                          <p><b>NOTE! - In most cases, please use the new copy function in the new build tool. You can access this from "View Overview" above.</b></p>
                          {/* <p>This will create a copy of this Content Module for use in future Courses and Course Amendments. It will NOT create the required Course or assign the Module to anything. PLEASE BE EXTREMELY CAREFUL using this button. If you have any questions at all please ask Eli/Grant/Erik.</p>
                          <p> This will also create copies of all Nodes, Scenes, Evaluations, etc associated with the Module being copied.</p> */}
                        </div>
                        <button className="yt-btn small block danger" onClick={() => this._handleCopyEvaluation(selectedContentModule._id)}>COPY CONTENT MODULE</button>
                      </div>
                    </div>
                  </div>
                  <ResourceLinkModal
                    closeModal={() => this.setState({createLinkModalOpen: false})}
                    dispatch={this.props.dispatch}
                    formType="Create"
                    isOpen={this.state.createLinkModalOpen}
                    refId={selectedContentModule._id}
                    refKey="_contentModule"
                    resourceLink={resourceLinkStore.defaultItem}
                    />
                </div>
              }
            </div>
          </section>
        </div>
      </AuthoringLayout>
    )
  }
}

AdminSingleContentModule.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  return {
    certificateTemplateMap: store.certificateTemplate.byId 
    , evaluationStore: store.evaluation
    , loggedInUser: store.user.loggedIn.user
    , metricStore: store.metric 
    , nodeStore: store.node
    , resourceLinkStore: store.resourceLink
    , sponsorStore: store.sponsor
    , studyStore: store.study
    , contentModuleStore: store.contentModule
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminSingleContentModule)
);



// WEBPACK FOOTER //
// modules/contentModule/views/AdminSingleContentModule.js.jsx